import type { BaseQueryFn } from '@reduxjs/toolkit/query'
import { createApi, retry } from '@reduxjs/toolkit/query/react'
import type { AxiosRequestConfig } from 'axios'

import http, { BASE_URL, CustomAxiosRequestConfig } from '@/config/http/http'
import {
  IError,
  isTransientFailure,
  shapeError
} from '@/config/http/http.helpers'
import { accessForbidden, sessionExpired } from '@/ducks/auth/auth.shared'

export interface AxiosBaseQueryArgs {
  url: string
  method: AxiosRequestConfig['method']
  data?: AxiosRequestConfig['data']
  params?: AxiosRequestConfig['params']
  headers?: AxiosRequestConfig['headers']
  noAuth?: boolean
  skipAuth?: boolean
  /**
   * Opt-in for endpoints that read data over POST (this API models several
   * reads that way). GETs are retried without any opt-in; mutations never are.
   */
  retryable?: boolean
  /**
   * When true, a 403 does not dispatch accessForbidden (used by soft probes
   * like auto-accept status that may 403 without ending the session UX).
   */
  ignoreAuthRedirectOn403?: boolean
}

const MAX_RETRIES = 2

const isRetryableRequest = (args: AxiosBaseQueryArgs): boolean =>
  args?.retryable === true || String(args?.method).toUpperCase() === 'GET'

const axiosBaseQuery =
  (
    { baseUrl }: { baseUrl: string } = { baseUrl: '' }
  ): BaseQueryFn<AxiosBaseQueryArgs, unknown, IError> =>
  async (
    {
      url,
      method,
      data,
      params,
      headers,
      skipAuth,
      noAuth,
      ignoreAuthRedirectOn403
    },
    { signal, dispatch }
  ) => {
    try {
      const result = await http({
        url: baseUrl + url,
        method,
        data,
        params,
        headers,
        skipAuth,
        noAuth,
        // So an abandoned query stops occupying a connection instead of
        // running to completion in the background.
        signal
      } as CustomAxiosRequestConfig)
      return { data: result.data }
    } catch (axiosError) {
      const error = shapeError(axiosError)

      // Only an authentication failure ends the session, and it does so with a
      // message: a silent token wipe made a single 401 during boot look like
      // the app had simply forgotten the login. A 403 means the token is fine
      // and this account is not allowed — signing the user out would be wrong.
      // (Requests that carry no token — login — report their own errors.)
      const isAuthenticatedRequest = !skipAuth && !noAuth
      if (isAuthenticatedRequest && error.status === '401') {
        dispatch(sessionExpired())
      } else if (
        isAuthenticatedRequest &&
        error.status === '403' &&
        !ignoreAuthRedirectOn403
      ) {
        dispatch(accessForbidden())
      }

      return {
        error: { ...error, transient: isTransientFailure(axiosError) }
      }
    }
  }

// RTK types `maxRetries` and `retryCondition` as mutually exclusive: when a
// condition is supplied it owns the attempt cap, hence the `attempt` check.
const baseQueryWithRetry = retry(axiosBaseQuery({ baseUrl: BASE_URL }), {
  retryCondition: (error, args, { attempt }) =>
    attempt <= MAX_RETRIES &&
    Boolean((error as unknown as IError)?.transient) &&
    isRetryableRequest(args as AxiosBaseQueryArgs)
})

export const rootApi = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithRetry,
  tagTypes: [
    'Orders',
    'Menu',
    'Performance',
    'Account',
    'Offers',
    'CartDiscount',
    'BuyXGetY',
    'Campaigns',
    'Store'
  ],
  endpoints(build) {
    return {
      query: build.query({ query: () => ({ url: '/query', method: 'get' }) }),
      mutation: build.mutation({
        query: () => ({ url: '/mutation', method: 'post' })
      })
    }
  }
})
